import {
  notifyCreditExhaustion,
} from "@tribunalea/ui/credit-exhaustion";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000";

export function createApi(tokenGetter) {
  const rawRequest = async (path, options = {}) => {
    const token = tokenGetter ? await tokenGetter() : null;
    let response;
    try {
      response = await fetch(`${API_URL}${path}`, {
        ...options,
        credentials: "include",
        headers: {
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          ...(options.body ? { "content-type": "application/json" } : {}),
          ...options.headers,
        },
      });
    } catch {
      throw new Error("No pudimos conectarnos con Tribunalea.");
    }
    const payload = await response.json().catch(() => ({}));
    notifyCreditExhaustion(payload);
    return { payload, response };
  };

  const responseError = (payload) => {
    const error = new Error(
      payload.error || "No pudimos completar la operación.",
    );
    error.code = payload.code;
    error.issues = payload.issues;
    return error;
  };

  const request = async (path, options = {}) => {
    const { payload, response } = await rawRequest(path, options);
    if (!response.ok) {
      throw responseError(payload);
    }
    return payload.data;
  };

  const reverifiableRequest = async (path, options = {}) => {
    const { payload, response } = await rawRequest(path, options);
    if (payload.clerk_error) return payload;
    if (!response.ok) throw responseError(payload);
    return payload.data;
  };

  const blobRequest = async (path) => {
    const token = tokenGetter ? await tokenGetter() : null;
    let response;
    try {
      response = await fetch(`${API_URL}${path}`, {
        credentials: "include",
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
    } catch {
      throw new Error("No pudimos conectarnos con Tribunalea.");
    }
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      notifyCreditExhaustion(payload);
      throw responseError(payload);
    }
    return {
      blob: await response.blob(),
      fileName: response.headers.get("content-disposition")
        ?.match(/filename="([^"]+)"/)?.[1] || "archivo",
    };
  };

  const uploadDoctrine = async (path, formData, onProgress, method = "POST") => {
    const token = tokenGetter ? await tokenGetter() : null;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, `${API_URL}${path}`);
      xhr.withCredentials = true;
      if (token) xhr.setRequestHeader("authorization", `Bearer ${token}`);
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onProgress?.(Math.round((event.loaded / event.total) * 100));
        }
      });
      xhr.addEventListener("load", () => {
        let payload = {};
        try {
          payload = JSON.parse(xhr.responseText || "{}");
        } catch {
          payload = {};
        }
        notifyCreditExhaustion(payload);
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(responseError(payload));
          return;
        }
        resolve(payload.data);
      });
      xhr.addEventListener("error", () => reject(
        new Error("No pudimos conectarnos con Tribunalea."),
      ));
      xhr.send(formData);
    });
  };

  return {
    profile: () => request("/api/profile"),
    updateProfile: (input) => request("/api/profile", {
      method: "PUT",
      body: JSON.stringify(input),
    }),
    updateLegalSignature: (input) => request("/api/profile/signature", {
      method: "PUT",
      body: JSON.stringify(input),
    }),
    whatsappLink: () => request("/api/whatsapp"),
    requestWhatsappVerification: (input) => request(
      "/api/whatsapp/verifications",
      { method: "POST", body: JSON.stringify(input) },
    ),
    confirmWhatsappVerification: (input) => request(
      "/api/whatsapp/verifications/confirm",
      { method: "POST", body: JSON.stringify(input) },
    ),
    unlinkWhatsapp: () => request("/api/whatsapp", { method: "DELETE" }),
    credits: () => request("/api/credits"),
    creditActivity: (days = 30) =>
      request(`/api/credits/activity?days=${encodeURIComponent(days)}`),
    billing: () => request("/api/billing"),
    billingAdminAccess: () => request("/api/billing/admin/access"),
    activityAdminAccess: () => request("/api/admin/user-activity/access"),
    creditGrantAccess: () => request("/api/admin/credit-grants/access"),
    searchCreditGrantTargets: (query) => request(
      `/api/admin/credit-grants/targets?q=${encodeURIComponent(query)}`,
    ),
    createFreeCreditGrant: (input) => reverifiableRequest(
      "/api/admin/credit-grants/grants",
      { method: "POST", body: JSON.stringify(input) },
    ),
    activityUsers: (query = "") => request(
      `/api/admin/user-activity/users?q=${encodeURIComponent(query)}&limit=20`,
    ),
    userActivityMetrics: (filters = {}) => {
      const query = new URLSearchParams(
        Object.entries(filters)
          .filter(([, value]) => value !== "" && value !== null && value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      );
      return request(`/api/admin/user-activity/metrics?${query}`);
    },
    userActivity: (filters = {}) => {
      const query = new URLSearchParams(
        Object.entries(filters)
          .filter(([, value]) => value !== "" && value !== null && value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      );
      return request(`/api/admin/user-activity?${query}`);
    },
    billingCatalog: () => request("/api/billing/admin/catalog"),
    monthlyFinancialReport: (month, { refresh = false } = {}) => request(
      `/api/billing/admin/monthly-report?month=${encodeURIComponent(month)}${refresh ? "&refresh=1" : ""}`,
    ),
    updateBillingCatalog: (input) => request("/api/billing/admin/catalog", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
    createCreditOrder: (input) => request("/api/billing/orders", {
      method: "POST",
      body: JSON.stringify(input),
    }),
    createSubscription: (input) => request("/api/billing/subscriptions", {
      method: "POST",
      body: JSON.stringify(input),
    }),
    reconcileBillingReturn: (reference) => request("/api/billing/return", {
      method: "POST",
      body: JSON.stringify({ reference }),
    }),
    cancelSubscription: () => request("/api/billing/subscription", {
      method: "DELETE",
    }),
    changeSubscription: (input) => request("/api/billing/subscription", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
    impersonationStatus: () => request("/api/support/impersonation"),
    invitationAccess: () => request("/api/admin/invitations/access"),
    invitations: () => request("/api/admin/invitations"),
    createInvitation: (input) => request("/api/admin/invitations", {
      method: "POST",
      body: JSON.stringify(input),
    }),
    revokeInvitation: (invitationId) => request(
      `/api/admin/invitations/${encodeURIComponent(invitationId)}`,
      { method: "DELETE" },
    ),
    institutionSummary: () => request("/api/institution"),
    institutionActivity: (page = 1) => request(
      `/api/institution/activity?page=${page}&limit=25`,
    ),
    updateOrganizationBranding: (input) => request(
      "/api/institution/branding",
      { method: "PUT", body: JSON.stringify(input) },
    ),
    updateOrganizationAccessPolicy: (input) => request(
      "/api/institution/access-policy",
      { method: "PUT", body: JSON.stringify(input) },
    ),
    updateOrganizationBillingProfile: (input) => request(
      "/api/institution/billing-profile",
      { method: "PUT", body: JSON.stringify(input) },
    ),
    organizationProvisioningStatus: () => request(
      "/api/organization-provisioning",
    ),
    activateOrganization: () => request(
      "/api/organization-provisioning/activate",
      { method: "POST" },
    ),
    searchImpersonationUsers: (query = "") => request(
      `/api/support/impersonation/users?q=${encodeURIComponent(query)}`,
    ),
    impersonationWorkspaces: (userId) => request(
      `/api/support/impersonation/users/${encodeURIComponent(userId)}/workspaces`,
    ),
    startImpersonation: (input) => reverifiableRequest("/api/support/impersonation", {
      method: "POST",
      body: JSON.stringify(input),
    }),
    endImpersonation: () => request("/api/support/impersonation", {
      method: "DELETE",
    }),
    doctrineAdminAccess: () => request("/api/admin/doctrine/access"),
    backgroundJobs: ({ historyHours = 168, limit = 50 } = {}) => request(
      `/api/admin/jobs?historyHours=${encodeURIComponent(historyHours)}&limit=${encodeURIComponent(limit)}`,
    ),
    doctrineDocuments: () => request("/api/admin/doctrine/documents"),
    createDoctrineDocument: (formData, onProgress) => uploadDoctrine(
      "/api/admin/doctrine/documents",
      formData,
      onProgress,
    ),
    createDoctrineVersion: (documentId, formData, onProgress) => uploadDoctrine(
      `/api/admin/doctrine/documents/${encodeURIComponent(documentId)}/versions`,
      formData,
      onProgress,
    ),
    updateDoctrineDocument: (documentId, input) => request(
      `/api/admin/doctrine/documents/${encodeURIComponent(documentId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    ),
    doctrineCover: (documentId) => blobRequest(
      `/api/admin/doctrine/documents/${encodeURIComponent(documentId)}/cover`,
    ),
    updateDoctrineCover: (documentId, file, onProgress) => {
      const formData = new FormData();
      formData.append("cover", file);
      return uploadDoctrine(
        `/api/admin/doctrine/documents/${encodeURIComponent(documentId)}/cover`,
        formData,
        onProgress,
        "PUT",
      );
    },
    deleteDoctrineCover: (documentId) => request(
      `/api/admin/doctrine/documents/${encodeURIComponent(documentId)}/cover`,
      { method: "DELETE" },
    ),
    doctrineTextPage: (versionId, page = 1) => request(
      `/api/admin/doctrine/versions/${encodeURIComponent(versionId)}/text?page=${encodeURIComponent(page)}`,
    ),
    doctrineOriginal: (versionId, download = false) => blobRequest(
      `/api/admin/doctrine/versions/${encodeURIComponent(versionId)}/file${download ? "?download=1" : ""}`,
    ),
    doctrineTextDownload: (versionId) => blobRequest(
      `/api/admin/doctrine/versions/${encodeURIComponent(versionId)}/text/download`,
    ),
    deleteDoctrineVersion: (versionId) => request(
      `/api/admin/doctrine/versions/${encodeURIComponent(versionId)}`,
      { method: "DELETE" },
    ),
    archiveDoctrineDocument: (documentId) => request(
      `/api/admin/doctrine/documents/${encodeURIComponent(documentId)}`,
      { method: "DELETE" },
    ),
  };
}
